import React, { useRef, useEffect } from 'react';

import { bind } from 'bind-event-listener';

import { CommentEditor as PlatformCommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { Box } from '@atlaskit/primitives/compiled';
import type { RenderFn } from '@atlassian/forge-ui-types';

import type { EditorProps } from '../../../types';
import { usePresetByFeatures } from '../utils/usePresetByFeatures';

export type CommentEditorProps = EditorProps & {
	/**
	 * Renders a Cancel button at the bottom of the editor. Handler is called when this button is clicked.
	 */
	onCancel?: () => void;
	/**
	 * Renders a Save button at the bottom of the editor. Handler is called when this button is clicked.
	 */
	onSave?: (value?: JSONDocNode) => void;
};

/**
 * The comment editor provides a contained editor UI with a simple toolbar.
 */
export const CommentEditor = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { onChange, defaultValue, features, isDisabled, onSave, onCancel } = props.forgeDoc
		.props as CommentEditorProps;

	const { preset, editorApi } = usePresetByFeatures('comment', features);

	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const element = wrapperRef.current;
		if (!element) {
			return;
		}
		return bind(element, { type: 'keypress', listener: (e) => e.stopImmediatePropagation() });
	}, []);

	return (
		<Box ref={wrapperRef}>
			<EditorContext>
				<PlatformCommentEditor
					disabled={isDisabled}
					defaultValue={defaultValue}
					preset={preset}
					onSave={
						onSave
							? () => {
									editorApi?.core.actions.requestDocument((jsonDocNode) => {
										onSave?.(jsonDocNode);
									});
								}
							: undefined
					}
					onCancel={onCancel ? () => onCancel() : undefined}
					onChange={() => {
						editorApi?.core.actions.requestDocument((jsonDocNode) => {
							onChange?.(jsonDocNode);
						});
					}}
				/>
			</EditorContext>
		</Box>
	);
};
